import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTournament } from '../context/TournamentContext';
import { getPlayerSide, calculateGameResult } from '../engine/scoring';
import { StatusBadge } from '../components/LiveIndicator';
import { User, LogIn } from 'lucide-react';

export function MyMatchesPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { games, getMatchUp, getTeam, getScoresByGame } = useTournament();

  if (!isAuthenticated || !user?.player) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><User size={48} /></div>
        <p className="mb-md">Přihlaste se pro zobrazení vašich her</p>
        <button className="btn btn-accent" onClick={() => navigate('/login')}>
          <LogIn size={16} /> Přihlásit se
        </button>
      </div>
    );
  }

  const playerId = user.player.id;
  const myGames = games.filter(g => getPlayerSide(playerId, g) !== null);

  return (
    <div>
      <h1 className="mb-md">
        <User size={24} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
        Moje hry
      </h1>
      <p className="text-secondary mb-lg" style={{ fontSize: '0.875rem' }}>
        Přihlášen jako <strong>{user.player.name}</strong>
      </p>

      {myGames.length > 0 ? (
        <div className="flex flex-col gap-md">
          {myGames.map(game => {
            const matchUp = getMatchUp(game.match_up_id);
            const teamA = matchUp?.team_a_id ? getTeam(matchUp.team_a_id) : null;
            const teamB = matchUp?.team_b_id ? getTeam(matchUp.team_b_id) : null;
            const side = getPlayerSide(playerId, game);
            const gameLabel = game.type === 'foursome' ? 'Foursome' : `Single ${game.game_number}`;

            return (
              <div
                key={game.id}
                className="card"
                onClick={() => navigate(`/game/${game.id}`)}
                style={{ cursor: 'pointer', borderLeft: `4px solid ${side === 'a' ? (teamA?.color ?? 'var(--side-a)') : (teamB?.color ?? 'var(--side-b)')}` }}
              >
                <div className="flex items-center justify-between mb-sm">
                  <div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {matchUp?.label} • {matchUp?.scheduled_time}
                    </span>
                  </div>
                  {(() => {
                    const scores = getScoresByGame(game.id);
                    const gameResult = calculateGameResult(game, scores);
                    if (gameResult.matchPlayState.isFinished) return <StatusBadge status="completed" />;
                    if (scores.length > 0) return <StatusBadge status="in_progress" />;
                    return <StatusBadge status="scheduled" />;
                  })()}
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{gameLabel}</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                      {teamA?.name ?? 'TBD'} vs {teamB?.name ?? 'TBD'}
                    </div>
                  </div>
                  {(() => {
                    const scores = getScoresByGame(game.id);
                    const gameResult = calculateGameResult(game, scores);
                    const mp = gameResult.matchPlayState;
                    if (mp.isFinished && mp.resultText) {
                      return (
                        <span style={{
                          fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1.125rem',
                          color: mp.winnerSide === side ? 'var(--success)' : mp.winnerSide === 'halved' ? 'var(--text-secondary)' : 'var(--error)',
                        }}>
                          {mp.resultText}
                        </span>
                      );
                    }
                    if (mp.holesPlayed > 0 && mp.lead > 0) {
                      return (
                        <span style={{
                          fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1rem',
                          color: 'var(--live)',
                        }}>
                          {mp.lead} {mp.leaderSide === side ? 'UP' : 'DN'}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
                {(() => {
                  const scores = getScoresByGame(game.id);
                  const gameResult = calculateGameResult(game, scores);
                  if (!gameResult.matchPlayState.isFinished) return (
                    <div className="mt-sm" style={{ fontSize: '0.8125rem', color: 'var(--accent)' }}>
                      Klikněte pro zadání výsledků →
                    </div>
                  );
                  return null;
                })()}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon">🏌️</div>
          <p>Zatím nemáte přiřazené žádné hry</p>
        </div>
      )}
    </div>
  );
}
